import { useFormik } from "formik";
import React, { useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import ValidatedForm from "components/forms/ValidatedFormWithoutFormik";
import nameof from "utils/nameof";
import { type AccountConfigurationInfo, type LimitedAccountInfo } from "features/Accounts/types";
import styles from "../AccountConfiguration.module.scss";
import { renderCustomLabel } from "components/multiSelect/helpers";
import { AccountInfoLayout } from "features/Accounts/Configuration/AccountInfoLayout";
import { EndpointsToGetSasLink } from "enums";
import ImageAspectRatio from "enums/imageAspectRatio";
import { type FormBindingRef } from "hooks/useFormBinding";
import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";

export interface LimitedAccountConfigurationProps {
  onDirtyChanged: (isDirty: boolean) => void;
  currentConfiguration: {
    name: string;
    logoUri: string;
    tags: string[];
  };
  onSubmit: (info: { tags: string[] }) => void;
  onAddNewTag: (tag: string) => void;
  existingTags: string[];
}

export const LimitedAccountConfiguration = forwardRef<FormBindingRef, LimitedAccountConfigurationProps>(
  (props, ref) => {
    const { currentConfiguration, onAddNewTag, onDirtyChanged, existingTags } = props;

    const formik = useFormik<{ tags: string[] }>({
      initialValues: { tags: currentConfiguration.tags },
      onSubmit: (values: { tags: string[] }) => {
        props.onSubmit({ tags: values.tags });
      },
      validateOnMount: true,
      enableReinitialize: true,
    });

    const { dirty, values, submitForm, resetForm } = formik;
    const { tags } = values;

    useImperativeHandle(ref, () => ({
      submitForm,
      resetForm,
    }));

    const accountsLabelsOptions: { text: string; value: string }[] = useMemo(
      () => existingTags.map((label) => ({ text: label, value: label })),
      [existingTags],
    );

    const handleAddNewTag = (_: React.ChangeEvent<HTMLInputElement>, { value }: { value: string }) => {
      onAddNewTag(value);
    };

    useEffect(() => {
      onDirtyChanged(dirty);
    }, [dirty, onDirtyChanged]);

    const handleLabelsChange = (_: string, value: { selected: string[] }) => {
      formik.setFieldValue("tags", value.selected);
    };

    return (
      <section className="account-info-form">
        <ValidatedForm
          unsavedChangesPrompt={{
            title: "Exit Without Saving?",
            message: "Are you sure you want to exit without saving this account? All information entered will be lost.",
          }}
          {...formik}
        >
          <>
            <AccountInfoLayout
              image={
                <ImagePreview
                  value={currentConfiguration.logoUri}
                  propertyName={nameof<AccountConfigurationInfo>("logoUri")}
                  label="Logo"
                  combinedIcon
                  placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
                  headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
                  endpointToGetSasLink={EndpointsToGetSasLink.Account}
                  disabled
                  isHidden
                  aspectRatio={ImageAspectRatio.SQUARE_1X1}
                  {...formik}
                />
              }
              name={
                <ValidatedForm.InputField
                  label="Company Name"
                  value={currentConfiguration.name}
                  propertyName={nameof<AccountConfigurationInfo>("name")}
                  placeholder="Enter Company Name"
                  disabled
                />
              }
            />
            <section className={styles.section}>
              <ValidatedForm.MultiSelectWithAdditionField
                {...formik}
                options={accountsLabelsOptions}
                label="Labels"
                value={tags}
                propertyName={nameof<LimitedAccountInfo>("tags")}
                onAddItem={handleAddNewTag}
                isCaseSensitive
                setFieldValue={handleLabelsChange}
                renderLabel={renderCustomLabel}
                placeholder={"Type to add label"}
              />
            </section>
          </>
        </ValidatedForm>
      </section>
    );
  },
);
